// EstadisticasReportPage.jsx - Vista de estadísticas/resultados con datos reales de BD
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS as LMS_ENDPOINTS } from '../../Lms/Utils/Endpoints';
import { Models } from '../../Lms/Utils/ModelsLMS';
import { cursosNormalize } from '../../Lms/Objetos/CursosModel';
import { useResourcePermissions } from '../../../utils/useResourcePermissions';
import './../../../style/Vars.css';
import './../../../style/ContainersStyles.css';
import './../../../style/ElementsStyle.css';
import AlertDialog from '../Components/Shared/AlertDialog';

const nombreTabla = "Estadísticas y Resultados";

export default function EstadisticasReportPage() {
    const { puedeExportar } = useResourcePermissions('Estadísticas');
    const [loading, setLoading] = useState(false);
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState(0); // 0 = TODOS
    const [students, setStudents] = useState([]);
    const [evaluations, setEvaluations] = useState([]); // Cuestionarios y exámenes
    const [gradesByStudent, setGradesByStudent] = useState({});

    // Estado para modal de alerta
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

    const currentCourse = useMemo(
        () => courses.find((c) => c.idCurso === selectedCourseId) || null,
        [courses, selectedCourseId]
    );

    // 1. Cargar lista de cursos
    useEffect(() => {
        const loadCourses = async () => {
            setLoading(true);
            try {
                const res = await axios.get(LMS_ENDPOINTS.cursos);
                const list = Array.isArray(res.data) ? res.data : [];
                const normalized = list
                    .map(cursosNormalize)
                    .filter((c) => Number.isFinite(Number(c.idCurso)));

                console.log('[EstadisticasReport] Cursos cargados:', normalized.length);
                setCourses(normalized);

                if (normalized.length && selectedCourseId == null) {
                    setSelectedCourseId(Number(normalized[0].idCurso));
                }
            } catch (e) {
                console.error('[EstadisticasReport] Error al cargar cursos:', e);
            } finally {
                setLoading(false);
            }
        };

        loadCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 2. Cargar datos del curso seleccionado
    useEffect(() => {
        const loadCourseData = async () => {
            if (selectedCourseId === null) {
                setStudents([]);
                setEvaluations([]);
                setGradesByStudent({});
                return;
            }

            setLoading(true);
            try {
                // Cargar datos necesarios
                const [
                    respEstudiantesCurso,
                    respEstudiantes,
                    respEmpleados,
                    respPersonas,
                    respModulos,
                    respModuloContenido,
                    respCuestionarios,
                    respExamenes,
                    respCuestionariosIntentos,
                    respExamenesIntentos
                ] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.estudiantesCurso),
                    axios.get(LMS_ENDPOINTS.estudiante),
                    axios.get(`${LMS_ENDPOINTS.estudiante.replace('/Estudiante', '')}/Empleados`),
                    axios.get(`${LMS_ENDPOINTS.estudiante.replace('/Estudiante', '')}/Personas`),
                    axios.get(LMS_ENDPOINTS.modulo),
                    axios.get(LMS_ENDPOINTS.moduloContenido),
                    axios.get(LMS_ENDPOINTS.cuestionario),
                    axios.get(LMS_ENDPOINTS.examen),
                    axios.get(LMS_ENDPOINTS.cuestionariosIntentos),
                    axios.get(LMS_ENDPOINTS.examenesIntentos)
                ]);

                // Normalizar datos
                const allEstudiantesCurso = (respEstudiantesCurso.data || []).map(Models.estudiantesCurso);
                const allEstudiantes = (respEstudiantes.data || []).map(Models.estudiante);
                const allEmpleados = respEmpleados.data || [];
                const allPersonas = respPersonas.data || [];
                const allModulos = (respModulos.data || []).map(Models.modulo);
                const allModuloContenido = (respModuloContenido.data || []).map(Models.moduloContenido);
                const allCuestionarios = respCuestionarios.data || [];
                const allExamenes = respExamenes.data || [];
                const allCuestionariosIntentos = respCuestionariosIntentos.data || [];
                const allExamenesIntentos = respExamenesIntentos.data || [];

                // Filtrar estudiantes del curso
                const estudiantesDelCurso = selectedCourseId === 0
                    ? allEstudiantesCurso
                    : allEstudiantesCurso.filter(
                        rel => Number(rel.idCurso) === Number(selectedCourseId)
                    );

                console.log('[EstadisticasReport] Estudiantes en el curso:', estudiantesDelCurso.length);

                // Obtener módulos del curso
                const modulosDelCurso = selectedCourseId === 0
                    ? allModulos
                    : allModulos.filter(m => Number(m.idCurso) === Number(selectedCourseId));

                // Obtener evaluaciones (cuestionarios y exámenes) del curso
                const evaluacionesDelCurso = [];

                modulosDelCurso.forEach(mod => {
                    const contenidos = allModuloContenido.filter(
                        mc => Number(mc.idModulo) === Number(mod.idModulo) && mc.activo
                    );

                    contenidos.forEach(mc => {
                        if (mc.tablaContenido === 'Cuestionario') {
                            const cuestionario = allCuestionarios.find(
                                q => Number(q.idCuestionario || q.IdCuestionario) === Number(mc.idAsociado)
                            );
                            if (cuestionario) {
                                evaluacionesDelCurso.push({
                                    id: cuestionario.idCuestionario || cuestionario.IdCuestionario,
                                    titulo: cuestionario.titulo || cuestionario.Titulo || 'Cuestionario',
                                    tipo: 'cuestionario',
                                    max: 100
                                });
                            }
                        } else if (mc.tablaContenido === 'Examen') {
                            const examen = allExamenes.find(
                                e => Number(e.idExamen || e.IdExamen) === Number(mc.idAsociado)
                            );
                            if (examen) {
                                evaluacionesDelCurso.push({
                                    id: examen.idExamen || examen.IdExamen,
                                    titulo: examen.titulo || examen.Titulo || 'Examen',
                                    tipo: 'examen',
                                    max: 100
                                });
                            }
                        }
                    });
                });

                console.log('[EstadisticasReport] Evaluaciones del curso:', evaluacionesDelCurso.length);
                setEvaluations(evaluacionesDelCurso);

                // Mapear estudiantes con información completa
                const studentsData = estudiantesDelCurso.map(rel => {
                    const studentId = rel.idEstudiante;
                    const cursoId = rel.idCurso;
                    const studentInfo = allEstudiantes.find(e => Number(e.idEstudiante) === Number(studentId));

                    let nombreCompleto = 'Estudiante Desconocido';
                    let email = '';

                    if (studentInfo) {
                        const empleado = allEmpleados.find(emp => Number(emp.idEmpleado || emp.IdEmpleado) === Number(studentInfo.idEmpleado));
                        if (empleado) {
                            const persona = allPersonas.find(p => Number(p.idPersona || p.IdPersona) === Number(empleado.idPersona || empleado.IdPersona));
                            if (persona) {
                                nombreCompleto = `${persona.nombre || ''} ${persona.apellidoPaterno || ''} ${persona.apellidoMaterno || ''}`.trim();
                                email = persona.email || persona.Email || '';
                            }
                        }
                    }

                    // Obtener nombre del curso
                    const curso = courses.find(c => c.idCurso === cursoId);
                    const nombreCurso = curso ? curso.nombre : 'Curso Desconocido';

                    return {
                        id: studentId,
                        idCurso: cursoId,
                        nombre: nombreCompleto,
                        nombreCurso: nombreCurso,
                        email: email
                    };
                });

                console.log('[EstadisticasReport] Estudiantes procesados:', studentsData.length);
                setStudents(studentsData);

                // Calcular calificaciones por estudiante
                const gradesMap = {};

                studentsData.forEach(student => {
                    gradesMap[student.id] = {};

                    evaluacionesDelCurso.forEach(evaluation => {
                        let calificacion = null;

                        if (evaluation.tipo === 'cuestionario') {
                            // Buscar último intento del cuestionario
                            const intentos = allCuestionariosIntentos.filter(
                                i => Number(i.idEstudiante || i.IdEstudiante) === Number(student.id) &&
                                    Number(i.idCuestionario || i.IdCuestionario) === Number(evaluation.id)
                            );

                            if (intentos.length > 0) {
                                // Obtener el último intento
                                const ultimoIntento = intentos.reduce((latest, current) => {
                                    const latestDate = new Date(latest.fechaIntento || latest.FechaIntento);
                                    const currentDate = new Date(current.fechaIntento || current.FechaIntento);
                                    return currentDate > latestDate ? current : latest;
                                });
                                calificacion = ultimoIntento.calificacion || ultimoIntento.Calificacion || 0;
                            }
                        } else if (evaluation.tipo === 'examen') {
                            // Buscar último intento del examen
                            const intentos = allExamenesIntentos.filter(
                                i => Number(i.idEstudiante || i.IdEstudiante) === Number(student.id) &&
                                    Number(i.idExamen || i.IdExamen) === Number(evaluation.id)
                            );

                            if (intentos.length > 0) {
                                const ultimoIntento = intentos.reduce((latest, current) => {
                                    const latestDate = new Date(latest.fechaIntento || latest.FechaIntento);
                                    const currentDate = new Date(current.fechaIntento || current.FechaIntento);
                                    return currentDate > latestDate ? current : latest;
                                });
                                calificacion = ultimoIntento.calificacion || ultimoIntento.Calificacion || 0;
                            }
                        }

                        gradesMap[student.id][evaluation.id] = calificacion;
                    });
                });

                console.log('[EstadisticasReport] Calificaciones calculadas');
                setGradesByStudent(gradesMap);

            } catch (e) {
                console.error('[EstadisticasReport] Error al cargar datos del curso:', e);
                setStudents([]);
                setEvaluations([]);
                setGradesByStudent({});
            } finally {
                setLoading(false);
            }
        };

        loadCourseData();
    }, [selectedCourseId]);

    const getGradeClass = (grade) => {
        if (grade == null) return "eval-grade-empty";
        if (grade < 60) return "eval-grade-bad";
        if (grade < 80) return "eval-grade-mid";
        return "eval-grade-good";
    };

    const calcAverage = (rowGrades) => {
        const valid = rowGrades.filter((g) => typeof g === "number");
        if (!valid.length) return null;
        const sum = valid.reduce((acc, g) => acc + g, 0);
        return Math.round(sum / valid.length);
    };

    // Exportar a Excel (CSV)
    const downloadExcel = () => {
        if (!students || students.length === 0) {
            setAlertDialog({
                open: true,
                title: 'Información',
                message: 'No hay datos para exportar.',
                variant: 'info'
            });
            return;
        }

        // Construir encabezados dinámicos
        const headers = selectedCourseId === 0
            ? ["Curso", "Alumno", "Email", ...evaluations.map(q => q.titulo), "Promedio"]
            : ["Alumno", "Email", ...evaluations.map(q => q.titulo), "Promedio"];

        const csvRows = [
            headers.join(","),
            ...students.map(st => {
                const rowGrades = evaluations.map(
                    (q) => gradesByStudent[st.id]?.[q.id] ?? null
                );
                const avg = calcAverage(rowGrades);

                const baseData = [
                    `"${st.nombre || ''}"`,
                    `"${st.email || ''}"`,
                    ...rowGrades.map(g => g == null ? '""' : `"${g}%"`),
                    avg == null ? '""' : `"${avg}%"`
                ];

                return selectedCourseId === 0
                    ? [`"${st.nombreCurso || ''}"`, ...baseData].join(",")
                    : baseData.join(",");
            })
        ];

        const csvString = "\uFEFF" + csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Estadisticas_${currentCourse?.nombre || 'Curso'}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>

                    <select
                        className="roles-select"
                        value={selectedCourseId || ''}
                        onChange={(e) => setSelectedCourseId(Number(e.target.value))}
                        style={{ minWidth: '250px' }}
                    >
                        <option value={0}>TODOS</option>
                        {courses.map((c) => (
                            <option key={c.idCurso} value={c.idCurso}>
                                {c.nombre}
                            </option>
                        ))}
                    </select>

                    {puedeExportar && (
                        <button className="roles-btn" onClick={downloadExcel} title="Descargar Excel">
                            📥 Descargar Excel
                        </button>
                    )}
                </div>

                {selectedCourseId !== 0 && currentCourse && (
                    <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', marginBottom: '1rem' }}>
                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>{currentCourse.nombre}</h3>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
                            {students.length} alumnos • {evaluations.length} evaluaciones
                        </p>
                    </div>
                )}

                {selectedCourseId === 0 && (
                    <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', marginBottom: '1rem' }}>
                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>Todos los Cursos</h3>
                        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
                            {students.length} alumnos • {evaluations.length} evaluaciones
                        </p>
                    </div>
                )}

                {loading ? (
                    <div className="catalog-loading">Cargando estadísticas...</div>
                ) : (
                    <div className="roles-table-wrap">
                        <div className="roles-table-scroller">
                            <table className="roles-table">
                                <thead>
                                    <tr>
                                        {selectedCourseId === 0 && <th style={{ minWidth: '200px' }}>Curso</th>}
                                        <th style={{ minWidth: '200px' }}>Alumno</th>
                                        {evaluations.map((q) => (
                                            <th key={q.id} style={{ minWidth: '120px' }}>
                                                <div>{q.titulo}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>/ {q.max}</div>
                                            </th>
                                        ))}
                                        <th style={{ minWidth: '100px' }}>Promedio</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.length === 0 ? (
                                        <tr>
                                            <td colSpan={evaluations.length + (selectedCourseId === 0 ? 3 : 2)} style={{ textAlign: 'center' }}>
                                                No hay alumnos matriculados en este curso.
                                            </td>
                                        </tr>
                                    ) : (
                                        students.map((st) => {
                                            const rowGrades = evaluations.map(
                                                (q) => gradesByStudent[st.id]?.[q.id] ?? null
                                            );
                                            const avg = calcAverage(rowGrades);
                                            return (
                                                <tr key={`${st.idCurso}-${st.id}`}>
                                                    {selectedCourseId === 0 && (
                                                        <td>
                                                            <div style={{ fontWeight: '500' }}>{st.nombreCurso || '—'}</div>
                                                        </td>
                                                    )}
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                            <div style={{
                                                                width: '36px',
                                                                height: '36px',
                                                                borderRadius: '50%',
                                                                background: '#3b82f6',
                                                                color: '#fff',
                                                                display: 'grid',
                                                                placeItems: 'center',
                                                                fontWeight: '600'
                                                            }}>
                                                                {st.nombre.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: '500' }}>{st.nombre}</div>
                                                                <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{st.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {evaluations.map((q) => {
                                                        const grade = gradesByStudent[st.id]?.[q.id] ?? null;
                                                        const cls = getGradeClass(grade);
                                                        return (
                                                            <td key={q.id} style={{ textAlign: 'center' }}>
                                                                {grade == null ? (
                                                                    <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                                                                        Pendiente
                                                                    </span>
                                                                ) : (
                                                                    <span style={{
                                                                        padding: '0.25rem 0.5rem',
                                                                        borderRadius: '6px',
                                                                        fontWeight: '600',
                                                                        background: cls === 'eval-grade-good' ? '#dcfce7' :
                                                                            cls === 'eval-grade-mid' ? '#fef3c7' : '#fee2e2',
                                                                        color: cls === 'eval-grade-good' ? '#166534' :
                                                                            cls === 'eval-grade-mid' ? '#854d0e' : '#991b1b'
                                                                    }}>
                                                                        {grade}%
                                                                    </span>
                                                                )}
                                                            </td>
                                                        );
                                                    })}

                                                    <td style={{ textAlign: 'center', fontWeight: '600' }}>
                                                        {avg == null ? "—" : (
                                                            <span style={{
                                                                padding: '0.25rem 0.5rem',
                                                                borderRadius: '6px',
                                                                background: getGradeClass(avg) === 'eval-grade-good' ? '#dcfce7' :
                                                                    getGradeClass(avg) === 'eval-grade-mid' ? '#fef3c7' : '#fee2e2',
                                                                color: getGradeClass(avg) === 'eval-grade-good' ? '#166534' :
                                                                    getGradeClass(avg) === 'eval-grade-mid' ? '#854d0e' : '#991b1b'
                                                            }}>
                                                                {avg}%
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }}></span>
                            <span>&lt; 60% reprobado</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f59e0b' }}></span>
                            <span>60–79% en progreso</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e' }}></span>
                            <span>80–100% aprobado</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de alerta */}
            <AlertDialog
                open={alertDialog.open}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
            />
        </div>
    );
}
